import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Navigate } from 'react-router-dom'
import Button from '@SiteComponents/Button'
import AIStatusBanner from '@SiteComponents/AIStatusBanner'
import StepDotIndicator from '@SiteComponents/StepDotIndicator'
import MailboxIllustration from './MailboxIllustration'
import OTPInput from './OTPInput'
import MailIcon from '@Images/mail-icon.svg'

import {
  getUserEmail,
  getHasConfirmedEmail,
  getUserId,
} from '@SiteModules/User/selectors'

import {
  createOTP,
  verifyOTP,
} from '@SiteModules/Authentication/actions'

import './OTPVerification.scss'

const OTP_STORAGE_VERSION = 'v2'
const OTP_REQUEST_VALID_FOR_MS = 15 * 60 * 1000
const LEGACY_OTP_STORAGE_KEY = 'otp_sent'

const otpStorageKey = (userId: number) => `onboarding-otp-sent:${OTP_STORAGE_VERSION}:${userId}`

const hasRecentOTPRequest = (userId: number) => {
  try {
    const sentAt = Number(sessionStorage.getItem(otpStorageKey(userId)))

    return sentAt > 0 && Date.now() - sentAt < OTP_REQUEST_VALID_FOR_MS
  } catch (_error) {
    return false
  }
}

const rememberOTPRequest = (userId: number) => {
  try {
    sessionStorage.removeItem(LEGACY_OTP_STORAGE_KEY)
    sessionStorage.setItem(otpStorageKey(userId), Date.now().toString())
  } catch (_error) {
    // OTP delivery must still work when browser storage is unavailable.
  }
}

const OTPVerification: React.FC = () => {
  const dispatch = useDispatch()

  const userId = useSelector(getUserId)
  const userEmail = useSelector(getUserEmail)
  const hasConfirmedEmail = useSelector(getHasConfirmedEmail)
  const shouldRenderOTPVerification = !hasConfirmedEmail

  const [otpValue, setOtpValue] = useState<string[]>(['', '', '', '', '', ''])
  const [lastSubmittedOtp, setLastSubmittedOtp] = useState<string | null>(null)

  useEffect(() => {
    if (userId <= 0 || hasRecentOTPRequest(userId)) {
      return
    }

    rememberOTPRequest(userId)
    dispatch(createOTP({}))
  }, [dispatch, userId])

  useEffect(() => {
    const otp = otpValue.join('')
    const isComplete = otpValue.every(d => d !== '')

    if (isComplete && otp !== lastSubmittedOtp) {
      setLastSubmittedOtp(otp)
      dispatch(verifyOTP({ otpCode: otp }))
    }

    if (!isComplete) {
      setLastSubmittedOtp(null)
    }
  }, [otpValue, lastSubmittedOtp])

  const handleOpenGmail = () => {
    window.open('https://mail.google.com', '_blank')
  }

  const handleOpenOutlook = () => {
    window.open('https://outlook.live.com', '_blank')
  }

  const handleResendCode = () => {
    rememberOTPRequest(userId)
    dispatch(createOTP({ shouldNotify: true }))
  }

  const renderOTPVerification = () => {
    return (
      <div className="OTPVerification">
        <div className="OTPVerification__main">
          <AIStatusBanner text="🔒 Analysis complete! Verify email to start rendering..."/>

          <div className="OTPVerification__step-wrapper">
            <StepDotIndicator currentStep={4} totalSteps={4} stepName="Email Verification" />
          </div>

          <div className="OTPVerification__content">
            <div className="OTPVerification__illustration-wrapper">
              <MailboxIllustration />
            </div>

            <div className="OTPVerification__heading">
              <h1 className="OTPVerification__main-heading">
                Almost there! Verify your email to unlock your project.
              </h1>
              <p className="OTPVerification__sub-heading">
                We sent a 6-digit code to{' '}
                <span className="OTPVerification__email-highlight">{userEmail}</span>.
              </p>
            </div>

            <OTPInput value={otpValue} onChange={setOtpValue} />

            <div className="OTPVerification__quick-access">
              <div className="OTPVerification__button-row">
                <Button
                  styleType="outline"
                  size="small"
                  caption="Open Gmail"
                  data-testid="onboarding-open-gmail"
                  icon={<MailIcon />}
                  onClick={handleOpenGmail}
                />
                <Button
                  styleType="outline"
                  size="small"
                  caption="Open Outlook"
                  data-testid="onboarding-open-outlook"
                  icon={<MailIcon />}
                  onClick={handleOpenOutlook}
                />
              </div>
            </div>

            <div className="OTPVerification__divider" />

            <div className="OTPVerification__resend-wrapper">
              <button
                className="OTPVerification__resend-btn"
                data-testid="onboarding-otp-resend"
                type="button"
                onClick={handleResendCode}
              >
                Didn&apos;t receive it?{' '}
                <span className="OTPVerification__resend-highlight">Resend code</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <React.Fragment>
      {shouldRenderOTPVerification ? renderOTPVerification() : <Navigate to="/" />}
    </React.Fragment>
  )
}

export default OTPVerification
